/** Presentation helpers shared across pages. */

export function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function formatMonthYear(value) {
  if (!value) return 'Present';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Present';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short' });
}

export function formatStipend(min, max, currency = 'EUR') {
  if (min == null && max == null) return 'Unpaid';
  const format = (value) =>
    new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  if (min != null && max != null && min !== max) return `${format(min)} – ${format(max)}`;
  return format(min ?? max);
}

export function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function humanizeWorkMode(mode) {
  return mode.charAt(0) + mode.slice(1).toLowerCase();
}

export function deadlineLabel(deadline) {
  if (!deadline) return 'No deadline';
  const days = Math.ceil((new Date(deadline).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  if (days < 0) return 'Closed';
  if (days === 0) return 'Closes today';
  if (days === 1) return 'Closes tomorrow';
  return `${days} days left`;
}

export function relativeTime(value) {
  const diffMs = Date.now() - new Date(value).getTime();
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(value);
}

const DAY_MS = 1000 * 60 * 60 * 24;

/** Whole days until a deadline (negative once it has passed); null without one. */
export function daysUntil(deadline) {
  if (!deadline) return null;
  const time = new Date(deadline).getTime();
  if (Number.isNaN(time)) return null;
  return Math.ceil((time - Date.now()) / DAY_MS);
}

/**
 * How long ago, as a job board says it: "today", "yesterday", "3 days ago",
 * "2 weeks ago", "1 month ago". Null when there is no date to go on.
 */
export function timeAgo(value) {
  if (!value) return null;
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) return null;
  const days = Math.floor((Date.now() - time) / DAY_MS);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) {
    const weeks = Math.floor(days / 7);
    return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
  }
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} ${months === 1 ? 'month' : 'months'} ago`;
  return 'over a year ago';
}

/** "Posted 3 days ago"; null without a date. */
export function postedAgo(value) {
  const ago = timeAgo(value);
  return ago ? `Posted ${ago}` : null;
}

/** "15 Oct": a near date in a footer, where the year goes without saying. */
export function formatDayMonth(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/**
 * Where the work happens, in one short phrase: "Remote", "Kolkata",
 * "Kolkata (Hybrid)". The location alone, or the mode when there is none.
 */
export function placeLabel({ work_mode: mode, location }) {
  if (mode === 'REMOTE') return 'Remote';
  if (!location) return mode ? humanizeWorkMode(mode) : null;
  return mode === 'HYBRID' ? `${location} (Hybrid)` : location;
}

/**
 * Free text written one item per line -- or with "-", "*", "•" or "1." in
 * front -- split into items for a bullet list. Markdown emphasis and links
 * are reduced to their text; nothing is ever rendered as HTML.
 */
export function textToItems(value) {
  if (!value) return [];
  return value
    .split(/\r?\n/)
    .map((line) =>
      line
        .replace(/^\s*(?:#{1,6}\s+|>\s*|[-*+•●▪–]\s+|\d+[.)]\s+)+/, '')
        .replace(/!?\[([^\]]+)\]\([^)]*\)/g, '$1')
        .replace(/(^|[^\w])(\*\*|__|\*|_|~~|`)(?=\S)(.+?)(?<=\S)\2(?!\w)/g, '$1$3')
        .replace(/<[^>]+>/g, '')
        .replace(/\s+/g, ' ')
        .trim(),
    )
    .filter((line) => line && !/^[-*_=#|]+$/.test(line));
}

/** "6 months", "1 month"; null without a duration. */
export function durationLabel(months) {
  if (!months) return null;
  return `${months} ${months === 1 ? 'month' : 'months'}`;
}

/** The stipend as a listing shows it: a range, one figure, or "Unpaid". */
export function stipendLabel(internship) {
  if (!internship.is_paid) return 'Unpaid';
  return formatStipend(internship.stipend_min, internship.stipend_max, internship.stipend_currency);
}
